import { inject, Injectable } from '@angular/core';
import { Observable, take } from 'rxjs';
import type { SimXUser } from '../../../shared/models/entities/simx-user.model';
import { UserDataService } from '../../../shared/services/user-data.service';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { Permission } from '../definitions/permission.enum';

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _userDataService = inject(UserDataService);

  authenticatedUserCan(permission: Permission): Observable<boolean> {
    return new Observable(observer => {
      this._authenticationService
        .getUser$()
        .pipe(take(1))
        .subscribe(user => {
          if (user) {
            this.userCan(user, permission).subscribe(isAllowed => {
              observer.next(isAllowed);
              observer.complete();
            });
          } else {
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  userCan(
    userInput: SimXUser | string,
    requestedPermission: Permission,
  ): Observable<boolean> {
    const user$: Observable<SimXUser> = new Observable(observer => {
      if (
        typeof userInput === 'string' ||
        !(userInput.role && userInput.role.permissions)
      ) {
        const userId = typeof userInput === 'string' ? userInput : userInput.id;
        this._userDataService
          .getUser(userId, {
            returnOptions: {
              include: {
                role: {
                  permissions: {},
                },
              },
            },
          })
          .subscribe(user => {
            observer.next(user);
            observer.complete();
          });
      } else {
        observer.next(userInput);
        observer.complete();
      }
    });

    return new Observable(observer => {
      user$.subscribe(user => {
        const userHasPermission = !!user.role!.permissions!.find(
          permission => permission.permission === requestedPermission,
        );
        observer.next(userHasPermission);
        observer.complete();
      });
    });
  }
}
